import { useMemo, useState } from "react";
import {
  LayoutDashboard,
  ClipboardCheck,
  Calendar,
  FileText,
  GraduationCap,
  Bot,
  Settings,
  LifeBuoy,
  CheckCircle2,
  User2Icon,
} from "lucide-react";
import { NavLink } from "../components/NavLink";
import logo from "../assets/BizVantage.png";
import { SupportTicketDialog } from "../components/SupportTicketDialog";

import {
  Sidebar,
  SidebarContent,
  SidebarGroup,
  SidebarGroupContent,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarHeader,
  SidebarFooter,
  useSidebar,
} from "../components/ui/sidebar";
import { useLanguage } from "../contexts/LanguageContext";

const baseNavItems = [
  { title: "Dashboard", url: "/dashboard", icon: LayoutDashboard },
  { title: "Assessments", url: "/assessments", icon: ClipboardCheck },
  // { title: "Calendar", url: "/calendar", icon: Calendar },
  { title: "Documents", url: "/documents", icon: FileText },
  // { title: "Education", url: "/education", icon: GraduationCap },
  // { title: "Chatbot", url: "/chatbot", icon: Bot },
];

export function AppSidebar() {
  const { state } = useSidebar();
  const { t } = useLanguage();
  const collapsed = state === "collapsed";
  const [supportOpen, setSupportOpen] = useState(false);
  // Start with one already submitted
  const [ticketCount, setTicketCount] = useState(1);
  const hasSubmitted = ticketCount > 0;
  const showAdmin = useMemo(() => {
    try {
      const raw = localStorage.getItem("bv.registerResponse");
      if (!raw) return false;
      const data = JSON.parse(raw);
      return (
        data?.superAdmin === true ||
        data?.user?.superAdmin === true ||
        data?.data?.superAdmin === true
      );
    } catch {
      return false;
    }
  }, []);
  const navItems = useMemo(
    () =>
      showAdmin
        ? [
            { title: t("Admin"), url: "/admin", icon: User2Icon },
            ...baseNavItems.map((item) => ({ ...item, title: t(item.title) })),
          ]
        : baseNavItems.map((item) => ({ ...item, title: t(item.title) })),
    [showAdmin, t],
  );

  const handleTicketSubmitted = () => {
    setTicketCount((c) => c + 1);
  };

  return (
    <>
      <Sidebar collapsible="icon">
        <SidebarHeader className="p-4 pb-6">
          <div className="flex items-center gap-3">
            <img
              src={logo}
              alt="BizVantage"
              className={
                collapsed
                  ? "h-10 w-10 object-contain"
                  : "h-[45px] object-contain"
              }
            />
          </div>
          {!collapsed && (
            <p className="mt-1 text-[10px] italic text-sidebar-foreground/60 text-center -ml-14">
              {t("Aim higher with the help of AI")}
            </p>
          )}
        </SidebarHeader>

        <SidebarContent>
          <SidebarGroup>
            <SidebarGroupContent>
              <SidebarMenu>
                {navItems.map((item) => (
                  <SidebarMenuItem key={item.title}>
                    <SidebarMenuButton asChild>
                      <NavLink
                        to={item.url}
                        end={item.url === "/"}
                        className="hover:bg-sidebar-accent/60 transition-colors"
                        activeClassName="bg-sidebar-accent text-sidebar-accent-foreground font-medium"
                      >
                        <item.icon className="mr-3 h-[18px] w-[18px] shrink-0" />
                        {!collapsed && <span>{item.title}</span>}
                      </NavLink>
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                ))}
              </SidebarMenu>
            </SidebarGroupContent>
          </SidebarGroup>
        </SidebarContent>

        <SidebarFooter>
          <SidebarMenu>
            <SidebarMenuItem>
              <SidebarMenuButton
                onClick={() => setSupportOpen(true)}
                className="hover:bg-sidebar-accent/60 transition-colors cursor-pointer"
              >
                <LifeBuoy className="mr-3 h-[18px] w-[18px] shrink-0" />
                {!collapsed && (
                  <>
                    <span>{t("Support Ticket")}</span>
                    {hasSubmitted && (
                      <CheckCircle2 className="ml-auto h-3.5 w-3.5 text-green-500 fill-green-500 shrink-0" />
                    )}
                  </>
                )}
                {collapsed && hasSubmitted && (
                  <CheckCircle2 className="absolute -top-1 -right-1 h-3 w-3 text-green-500 fill-green-500" />
                )}
              </SidebarMenuButton>
            </SidebarMenuItem>
            <SidebarMenuItem>
              <SidebarMenuButton asChild>
                <NavLink
                  to="/settings"
                  className="hover:bg-sidebar-accent/60 transition-colors"
                  activeClassName="bg-sidebar-accent text-sidebar-accent-foreground font-medium"
                >
                  <Settings className="mr-3 h-[18px] w-[18px] shrink-0" />
                  {!collapsed && <span>{t("Settings")}</span>}
                </NavLink>
              </SidebarMenuButton>
            </SidebarMenuItem>
          </SidebarMenu>
        </SidebarFooter>
      </Sidebar>

      <SupportTicketDialog
        open={supportOpen}
        onOpenChange={setSupportOpen}
        onSubmitted={handleTicketSubmitted}
      />
    </>
  );
}
